import React, { useState } from "react";
import { ArrowUpRight, Mail, Phone, MapPin, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { submitLead } from "@/lib/api";

export const Contact = () => {
    const [form, setForm] = useState({
        name: "", email: "", phone: "", city: "",
        interest: "general",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await submitLead({ ...form, source_page: "contact" });
            toast.success("Message received. E3 team will reach out shortly.");
            setSubmitted(true);
            setForm({ name: "", email: "", phone: "", city: "", interest: "general", message: "" });
        } catch (err) {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <section className="pt-24 pb-16 md:pt-32 md:pb-20 px-6 md:px-10">
                <div className="max-w-7xl mx-auto">
                    <div className="e3-overline mb-6">Contact</div>
                    <h1 data-testid="contact-hero-title" className="font-serif text-5xl md:text-7xl lg:text-8xl font-light tracking-tighter leading-[0.95] text-stone-900 max-w-5xl mb-10">
                        Let's <span className="italic e3-gradient-text">talk.</span>
                    </h1>
                    <p className="text-stone-600 text-lg md:text-xl leading-relaxed max-w-3xl">
                        Whether you're an investor, potential franchise partner, hotel or fleet
                        operator — we'd love to hear from you.
                    </p>
                </div>
            </section>

            <section className="pb-24 md:pb-32 px-6 md:px-10">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12">
                    {/* Left: Info */}
                    <div className="md:col-span-5 space-y-8">
                        <div>
                            <div className="e3-overline mb-3">E3 Team</div>
                            <p className="text-stone-600 leading-relaxed">
                                Our partner desk is currently being staffed. All enquiries are actively
                                responded to within one business day.
                            </p>
                        </div>

                        <div className="space-y-6">
                            <ContactRow
                                icon={Mail}
                                label="Email"
                                value="info@e3charge.network"
                                href="mailto:info@e3charge.network"
                                testid="contact-info-email"
                            />
                            <ContactRow
                                icon={Phone}
                                label="Phone"
                                value="+91 · Coming Soon"
                                sub="Partner desk launching soon"
                                testid="contact-info-phone"
                            />
                            <ContactRow
                                icon={MapPin}
                                label="Head Office"
                                value="Gujarat · India"
                                sub="Full address publishing soon"
                                testid="contact-info-address"
                            />
                            <ContactRow
                                icon={MessageSquare}
                                label="Media & Press"
                                value="press@e3charge.network"
                                href="mailto:press@e3charge.network"
                                testid="contact-info-press"
                            />
                        </div>

                        <div className="e3-card-static bg-emerald-50 border-emerald-200">
                            <div className="e3-overline mb-2 text-emerald-800">Fastest Response</div>
                            <p className="text-stone-800 text-sm leading-relaxed">
                                Investors and franchise applicants get priority routing. Use the form
                                and select your interest to reach the right team directly.
                            </p>
                        </div>
                    </div>

                    {/* Right: Form */}
                    <div className="md:col-span-7">
                        {submitted ? (
                            <div className="e3-card-static bg-emerald-50 border-emerald-200 text-center py-16" data-testid="contact-form-success">
                                <div className="font-serif text-3xl text-stone-900 mb-3">Thank you.</div>
                                <p className="text-stone-700 mb-8">Your message is with the E3 team. We'll reach out shortly.</p>
                                <button onClick={() => setSubmitted(false)} className="e3-btn-ghost">Send another message</button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="e3-card-static space-y-5" data-testid="contact-form">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <FormField label="Full Name" required>
                                        <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid="contact-form-name" />
                                    </FormField>
                                    <FormField label="Email" required>
                                        <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" data-testid="contact-form-email" />
                                    </FormField>
                                    <FormField label="Phone" required>
                                        <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 ·············" data-testid="contact-form-phone" />
                                    </FormField>
                                    <FormField label="City / State">
                                        <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="e.g. Mumbai, MH" data-testid="contact-form-city" />
                                    </FormField>
                                </div>

                                <FormField label="I'm interested in" required>
                                    <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
                                        <SelectTrigger data-testid="contact-form-interest"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="investor">Investor · Owning stations</SelectItem>
                                            <SelectItem value="franchise">Franchise Partner</SelectItem>
                                            <SelectItem value="destination_partner">Destination Partner · Hotel / Resort</SelectItem>
                                            <SelectItem value="fleet">Fleet Operator</SelectItem>
                                            <SelectItem value="general">General Enquiry / Media</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </FormField>

                                <FormField label="Message" required>
                                    <Textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your interest, location, timeline…" data-testid="contact-form-message" />
                                </FormField>

                                <button type="submit" disabled={submitting} className="e3-btn-primary w-full justify-center" data-testid="contact-form-submit">
                                    {submitting ? "Sending…" : "Send Message"} <ArrowUpRight className="w-4 h-4" />
                                </button>
                                <p className="text-xs text-stone-500">
                                    By submitting, you agree to be contacted by the E3 team.
                                </p>
                            </form>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
};

const ContactRow = ({ icon: Icon, label, value, sub, href, testid }) => {
    const inner = (
        <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl e3-gradient-soft flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5 text-emerald-700" strokeWidth={1.5} />
            </div>
            <div>
                <div className="e3-overline mb-1">{label}</div>
                <div className="text-stone-900 font-medium">{value}</div>
                {sub && <div className="text-xs text-stone-500 mt-1">{sub}</div>}
            </div>
        </div>
    );
    return href ? (
        <a href={href} className="block hover:opacity-70 transition" data-testid={testid}>{inner}</a>
    ) : (
        <div data-testid={testid}>{inner}</div>
    );
};

const FormField = ({ label, required, children }) => (
    <div>
        <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider mb-2">
            {label} {required && <span className="text-emerald-600">*</span>}
        </label>
        {children}
    </div>
);
